import { createApi } from '@reduxjs/toolkit/query/react';
import { z } from 'zod';
import { baseQuery } from './baseQuery';
import { validateResponse } from './validateResponse';

// ---------------------------------------------------------------------------
// Request schema
// ---------------------------------------------------------------------------

export const ExUsSearchV3RequestSchema = z.object({
    search: z.object({
        firstName:   z.string().optional(),
        lastName:    z.string().optional(),
        city:        z.string().optional(),
        countryCode: z.string().optional(),
        state:       z.string().optional(),
        postalCode:  z.string().optional(),
        profId:      z.string().optional(),
        degree:      z.string().optional(),
        specialty:   z.string().optional(),
        universe:    z.string().optional(),
    }),
    user: z.object({
        team:      z.string().optional(),
        territory: z.string().optional(),
    }),
    pageNumber: z.number().int().min(1).default(1),
    pageSize:   z.number().int().min(1).max(100).default(20),
});

export type ExUsSearchV3Request = z.infer<typeof ExUsSearchV3RequestSchema>;

// ---------------------------------------------------------------------------
// Response row schema — mirrors ExUsSearchRowV3 C# class (camelCase from JSON)
// ---------------------------------------------------------------------------

const ns = z.string().nullish();
const nn = z.number().nullish();

export const ExUsSearchRowV3Schema = z.object({
    profId:           nn,
    profIdNew:        nn,
    status:           ns,
    firstName:        ns,
    middleName:       ns,
    lastName:         ns,
    suffix:           ns,
    title:            ns,
    degree:           ns,
    specialty:        ns,
    otherSpecialty:   ns,
    taxonomyCode:     ns,
    gender:           ns,
    address1:         ns,
    address2:         ns,
    city:             ns,
    state:            ns,
    postalCode:       ns,
    countryCode:      ns,
    addrType:         ns,
    priAddress1:      ns,
    priAddress2:      ns,
    priCity:          ns,
    priState:         ns,
    priZip5:          ns,
    phone:            ns,
    mobile:           ns,
    fax:              ns,
    email:            ns,
    website:          ns,
    orgName:          ns,
    orgId:            nn,
    universeFlag:     ns,
    aggspendFlag:     ns,
    aggspendStatus:   ns,
    ctmsFlag:         ns,
    pdrpOptout:       ns,
    noContact:        ns,
    universeFlagTm13: ns,
    universeFlagTm14: ns,
    universeFlagTm21: ns,
    universeFlagTm31: ns,
    universeFlagTm32: ns,
    universeFlagTm52: ns,
    universeFlagTm53: ns,
    universeFlagTm54: ns,
    universeFlagTm61: ns,
    universeFlagTm64: ns,
    universeFlagTm81: ns,
    universeFlagTm82: ns,
    terrId:           ns,
    mdmOverride:      ns,
    mdmTerrId:        ns,
    registrationId:      ns,
    registrationCountry: ns,
    medicalRegistry:     ns,
    nationality:         ns,
    birthYear:           ns,
    melissaEngineInfo:   ns,
    recordSource:        ns,
});

export type ExUsSearchRowV3 = z.infer<typeof ExUsSearchRowV3Schema>;

// ---------------------------------------------------------------------------
// Paginated response envelope
// ---------------------------------------------------------------------------

export const ExUsSearchV3ResponseSchema = z.object({
    rows:       ExUsSearchRowV3Schema.array(),
    totalCount: z.number().int(),
});

export type ExUsSearchV3Response = z.infer<typeof ExUsSearchV3ResponseSchema>;

// ---------------------------------------------------------------------------
// RTK Query slice
// ---------------------------------------------------------------------------

export const exUsProfessionalsV3Api = createApi({
    reducerPath: 'exUsProfessionalsV3Api',
    baseQuery,
    endpoints: (builder) => ({
        searchExUsV3: builder.mutation<ExUsSearchV3Response, ExUsSearchV3Request>({
            query: (body) => ({
                url:    'api/v3/exusprofessionals/search',
                method: 'POST',
                body,
            }),
            transformResponse: validateResponse(ExUsSearchV3ResponseSchema),
        }),
    }),
});

export const { useSearchExUsV3Mutation } = exUsProfessionalsV3Api;

